import { useRoute, useRouter } from 'vue-router'
import useGet from '../../../core/composables/api/useGet'
import { computed, onMounted } from 'vue'
import { FeedbackTopicType, type FeedbackTopic } from '../../../core/models/feedback-topic'
import type { Dialog } from '../../../core/models/dialog/dialog'
import usePut from '../../../core/composables/api/usePut'
import { ApiRequestStatus } from '../../../core/models/api/api-request-status'
import { useSnackStore } from '../../../stores/snack.store'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '@/core/events/event-key'

export const useReleasedDialog = () => {
  const route = useRoute()
  const { data, load, status, reload } = useGet<ReleasedDialog>(`dialog/${route.params.id}`)
  const router = useRouter()

  const { showError, showInfo } = useSnackStore()

  onMounted(() => {
    load()
  })

  const eventBus = useEventBus(EventKey.dialogSlideUpdated)
  eventBus.on(() => reload())

  const conductDialog = async () => {
    const { mutate, status: conductStatus } = usePut(`dialog/conduct/${route.params.id}`)

    await mutate(route.params.id as string)

    if (conductStatus.value == ApiRequestStatus.Success) {
      router.push({ name: 'coachee', params: { id: data.value?.dialog.coachee.id } })
      showInfo('Dialog abgeschlossen')
    } else {
      showError('Beim Abschliessen hat es einen Fehler gegeben.')
    }
  }

  const retractDialog = async () => {
    const { mutate, status: retractStatus } = usePut(`dialog/coachee-retract/${route.params.id}`)

    await mutate(route.params.id as string)

    if (retractStatus.value == ApiRequestStatus.Success) {
      router.push({ name: 'coachee', params: { id: data.value?.dialog.coachee.id } })
      showInfo('Dialog zurückgezogen')
    } else {
      showError('Beim Zurückziehen hat es einen Fehler gegeben.')
    }
  }

  const { mutate: mutateCoachRetract, status: coachRetractStatus } = usePut<string, string>(
    `dialog/coach-retract/${route.params.id}`,
  )

  const dialogUpdatedBus = useEventBus(EventKey.dialogUpdated)

  const goToFirstSlideAndReload = async () => {
    const dialogId = route.params.id as string
    await reload()
    eventBus.emit()
    await router.replace({
      name: 'start-slide',
      params: { id: dialogId },
    })
  }

  const coachRetractDialog = async () => {
    await mutateCoachRetract(route.params.id as string)

    if (coachRetractStatus.value == ApiRequestStatus.Success) {
      dialogUpdatedBus.emit({ dialogId: route.params.id as string })
      await goToFirstSlideAndReload()
      showInfo('Gespräch zurückgenommen')
      return true
    }

    showError('Beim Zurücknehmen hat es einen Fehler gegeben.')
    return false
  }

  const unfinishedTopics = computed(() => {
    if (data.value) {
      return data.value.feedbackTopics.filter(
        (topic) => topic.savedAt == undefined && topic.type != FeedbackTopicType.Journal,
      )
    }
    return []
  })

  const unfinishedSurveys = computed(() => {
    if (data.value) {
      return data.value.selectedSurveys.filter((survey) => !survey.isFinished)
    }
    return []
  })

  const { mutate, status: releaseStatus } = usePut<string, string>(
    `dialog/coachee-release/${route.params.id}`,
  )

  const { mutate: mutateCoachRelease, status: coachReleaseStatus } = usePut<string, string>(
    `dialog/coach-release/${route.params.id}`,
  )

  const canReleaseDialog = computed(() => {
    return unfinishedTopics.value.length == 0
  })

  const releaseDialog = async () => {
    if (!canReleaseDialog.value) {
      return
    }

    await mutate(route.params.id as string)

    if (releaseStatus.value == ApiRequestStatus.Success) {
      router.push({ name: 'dialogs' })
      showInfo('Dialog freigegeben')
    } else {
      showInfo('Dialog konnte nicht freigegeben werden')
    }
  }

  const coachReleaseDialog = async () => {
    await mutateCoachRelease(route.params.id as string)

    if (coachReleaseStatus.value == ApiRequestStatus.Success) {
      dialogUpdatedBus.emit({ dialogId: route.params.id as string })
      await goToFirstSlideAndReload()
      showInfo('Dialog freigegeben')
      return true
    }

    showError('Dialog konnte nicht freigegeben werden')
    return false
  }

  return {
    data,
    status,
    conductDialog,
    retractDialog,
    unfinishedTopics,
    unfinishedSurveys,
    releaseDialog,
    releaseStatus,
    canReleaseDialog,
    coachReleaseDialog,
    coachReleaseStatus,
    coachRetractDialog,
    coachRetractStatus,
  }
}

export interface ReleasedDialog {
  readonly dialog: Dialog
  readonly feedbackTopics: ReadonlyArray<FeedbackTopic & { savedAt?: string }>
  readonly selectedSurveys: ReadonlyArray<{
    id: string
    isFinished: boolean
  }>
}
